import React, { useEffect, useRef, useState } from 'react';
import { useLang } from '../i18n/LanguageContext';
import { Play, Volume2, VolumeX } from 'lucide-react';

const VIDEO_URL =
  'https://customer-assets.emergentagent.com/job_fiberplast-new/artifacts/np7tzwdm_fiberplast_v3_4_640x360.mp4';

const LOW_VOLUME = 0.15; // low but audible

const VideoShowcase = () => {
  const { t } = useLang();
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(true);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.volume = LOW_VOLUME;
    }
  }, []);

  const toggleMute = () => {
    const v = videoRef.current;
    if (!v) return;
    const next = !muted;
    v.muted = next;
    v.volume = LOW_VOLUME;
    if (!next) {
      // browser sometimes pauses when unmuting; ensure playback continues
      const p = v.play();
      if (p && typeof p.catch === 'function') p.catch(() => {});
    }
    setMuted(next);
  };

  return (
    <section className="py-24 bg-[#f5f7fa]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-14 items-center">
        <div>
          <p className="text-xs tracking-[0.3em] text-[#c53114] font-semibold uppercase inline-flex items-center gap-2">
            <Play size={12} /> {t('video.eyebrow', 'Video')}
          </p>
          <h2
            className="mt-3 text-3xl md:text-5xl font-semibold text-[#0b2545] leading-tight"
            style={{ fontFamily: "'Barlow Condensed', 'Oswald', sans-serif" }}
          >
            {t('video.title', 'FIBER PLAST IN ACTION')}
          </h2>
          <p className="mt-5 text-[#475569] leading-relaxed max-w-lg">
            {t(
              'video.body',
              'Scopri la nostra tecnologia Filament Winding proprietaria e i processi produttivi che rendono unici i manufatti FIBER PLAST.'
            )}
          </p>

          <div className="mt-8 grid grid-cols-3 gap-4 max-w-md">
            {[
              { k: '45+', v: t('video.stat1', 'Anni') },
              { k: '30+', v: t('video.stat2', 'Paesi') },
              { k: '5000', v: t('video.stat3', 'mm max') },
            ].map((s) => (
              <div key={s.v} className="border-l-2 border-[#c53114] pl-3">
                <p
                  className="text-2xl font-semibold text-[#0b2545] leading-none"
                  style={{ fontFamily: "'Barlow Condensed', 'Oswald', sans-serif" }}
                >
                  {s.k}
                </p>
                <p className="text-[10px] tracking-[0.25em] text-[#475569] uppercase mt-1">{s.v}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="relative w-full mx-auto lg:ml-auto max-w-[640px]">
          <div className="aspect-video w-full overflow-hidden shadow-2xl bg-[#0b2545] relative group">
            <video
              ref={videoRef}
              src={VIDEO_URL}
              autoPlay
              loop
              muted={muted}
              playsInline
              preload="metadata"
              className="w-full h-full object-contain"
            />
            <div className="pointer-events-none absolute inset-0 ring-1 ring-white/10" />
            <button
              type="button"
              onClick={toggleMute}
              aria-label={muted ? 'Attiva audio' : 'Disattiva audio'}
              className="absolute bottom-3 right-3 w-11 h-11 rounded-full bg-white/90 hover:bg-white text-[#0b2545] flex items-center justify-center shadow-md transition-colors"
            >
              {muted ? <VolumeX size={20} /> : <Volume2 size={20} />}
            </button>
          </div>
          {/* Accent block */}
          <div className="absolute -bottom-5 -left-5 hidden md:block w-24 h-24 border-2 border-[#c53114] -z-0" />
          <div className="absolute -top-5 -right-5 hidden md:block w-24 h-24 bg-[#0b2545] -z-0" />
        </div>
      </div>
    </section>
  );
};

export default VideoShowcase;
